import { z } from "zod";
import { RANKS } from "./ranks";

/*
  DOJO TRIALS. One row per rank you can earn, in rank order (Seedling is where
  everyone starts, so it has no trial). A trial is the same for every child:
  no items, no modifiers, no adapting.

  rank:         the rank this trial earns (must match a name in ranks.ts)
  seconds:      time limit
  passMark:     correct answers needed to pass
  mixPassMark:  the pass mark for Mix, which is harder (about 15% lower)
  stripes:      three marks below the pass mark that earn stripes on the way
                (70, 80 and 90% of the pass mark)
  mixStripes:   the same for Mix
  mix:          percentage of questions from each tier (1 easiest to 5 hardest)

  warmUp:       how many questions at the start come from tier 1 only, before
                the mix takes over. The same for every child, so a trial never
                opens with a hard question and the measure stays honest.

  Version 2 (v1.1) smooths the ladder: harder tiers come in a little at a
  time, with smaller pass-mark steps where they do. Version 3 (Phase 4.5)
  eases the climb: the pace tightens by at most about 20% a rank, the two
  easiest trials are almost all tier 1, and every trial warms up. Every trial
  stays at three minutes. Tune from real play: the history records the score,
  wrong answers and time per tier for every attempt. Bump TRIALS_VERSION when
  the numbers change, so old results can be told apart.
*/

export const TRIALS_VERSION = 3;

const MixSchema = z
  .object({
    1: z.number().nonnegative().optional(),
    2: z.number().nonnegative().optional(),
    3: z.number().nonnegative().optional(),
    4: z.number().nonnegative().optional(),
    5: z.number().nonnegative().optional(),
  })
  .refine(
    (m) => Object.values(m).reduce<number>((a, b) => a + (b ?? 0), 0) === 100,
    "mix must total 100",
  );

const Stripes = z.tuple([
  z.number().int().positive(),
  z.number().int().positive(),
  z.number().int().positive(),
]);
export type StripeMarks = Readonly<z.infer<typeof Stripes>>;

const stripesFit = ([a, b, c]: StripeMarks, pass: number) => a < b && b < c && c < pass;

const TrialRowSchema = z
  .object({
    rank: z.string(),
    seconds: z.number().int().positive(),
    passMark: z.number().int().positive(),
    mixPassMark: z.number().int().positive(),
    stripes: Stripes,
    mixStripes: Stripes,
    mix: MixSchema,
    warmUp: z.number().int().nonnegative().max(20),
  })
  .refine((r) => r.mixPassMark <= r.passMark, "the Mix pass mark can't be above the pass mark")
  .refine(
    (r) => stripesFit(r.stripes, r.passMark) && stripesFit(r.mixStripes, r.mixPassMark),
    "stripes must increase and sit below the pass mark",
  );
export type TrialRow = z.infer<typeof TrialRowSchema>;

const increasing = (xs: number[]) => xs.every((x, i) => i === 0 || x > xs[i - 1]!);

export const TrialsSchema = z
  .array(TrialRowSchema)
  .refine(
    (rows) =>
      rows.length === RANKS.length - 1 && rows.every((r, i) => r.rank === RANKS[i + 1]?.name),
    "one trial per rank after the first, in rank order",
  )
  .refine(
    (rows) => increasing(rows.map((r) => r.passMark)) && increasing(rows.map((r) => r.mixPassMark)),
    "pass marks must strictly increase",
  );

export const TRIALS: readonly TrialRow[] = TrialsSchema.parse([
  {
    rank: "Bamboo Sprout",
    seconds: 180,
    passMark: 9,
    mixPassMark: 8,
    stripes: [6, 7, 8],
    mixStripes: [5, 6, 7],
    mix: { 1: 100 },
    warmUp: 5,
  },
  {
    rank: "Shadow Cub",
    seconds: 180,
    passMark: 15,
    mixPassMark: 13,
    stripes: [11, 12, 14],
    mixStripes: [9, 10, 12],
    mix: { 1: 85, 2: 15 },
    warmUp: 5,
  },
  {
    rank: "Silent Step",
    seconds: 180,
    passMark: 20,
    mixPassMark: 17,
    stripes: [14, 16, 18],
    mixStripes: [12, 14, 15],
    mix: { 1: 65, 2: 30, 3: 5 },
    warmUp: 5,
  },
  {
    rank: "Swift Star",
    seconds: 180,
    passMark: 26,
    mixPassMark: 22,
    stripes: [18, 21, 23],
    mixStripes: [15, 18, 20],
    mix: { 1: 55, 2: 35, 3: 10 },
    warmUp: 5,
  },
  {
    rank: "Storm Ninja",
    seconds: 180,
    passMark: 33,
    mixPassMark: 28,
    stripes: [23, 26, 30],
    mixStripes: [20, 22, 25],
    mix: { 1: 50, 2: 35, 3: 15 },
    warmUp: 5,
  },
  {
    rank: "Fire Blade",
    seconds: 180,
    passMark: 40,
    mixPassMark: 34,
    stripes: [28, 32, 36],
    mixStripes: [24, 27, 31],
    mix: { 1: 40, 2: 35, 3: 20, 4: 5 },
    warmUp: 5,
  },
  {
    rank: "Dragon Master",
    seconds: 180,
    passMark: 49,
    mixPassMark: 42,
    stripes: [34, 39, 44],
    mixStripes: [29, 34, 38],
    mix: { 1: 30, 2: 35, 3: 25, 4: 10 },
    warmUp: 5,
  },
  {
    rank: "Legend",
    seconds: 180,
    passMark: 60,
    mixPassMark: 51,
    stripes: [42, 48, 54],
    mixStripes: [36, 41, 46],
    mix: { 1: 20, 2: 30, 3: 30, 4: 15, 5: 5 },
    warmUp: 5,
  },
]);
